import { ApiError, type LogQueryParams } from '@central/api';
import {
  Card,
  LogLines,
  PageHeader,
  color,
  downloadText,
  logFilterErrors,
  logLevelOptions,
  logLinesText,
  logRangeBounds,
  logRangeOptions,
  type LogLevel,
  type LogRange,
} from '@central/ui';
import { Alert, Autocomplete, Button, Select, TextInput } from '@mantine/core';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { api, deploymentQuery, deploymentsQuery } from '../api';
import { QueryState } from '../components/QueryState';
import { errorMessage } from '../format';

interface Draft {
  environmentId: string;
  service: string;
  level: '' | LogLevel;
  range: LogRange;
  contains: string;
}

const initialDraft: Omit<Draft, 'environmentId'> = {
  service: '',
  level: 'error',
  range: '24h',
  contains: '',
};

function toParams(d: Draft): LogQueryParams {
  return {
    environmentId: d.environmentId,
    service: d.service || undefined,
    level: d.level || undefined,
    contains: d.contains || undefined,
    ...logRangeBounds(d.range),
  };
}

/**
 * Customer logs (docs/design/CustomerLogs.dc.html). Offers only dedicated
 * deployments that show logs; whether the organization shares logs at all
 * (logAccess) is known only from the query's answer.
 */
export function Logs() {
  const deployments = useQuery(deploymentsQuery);
  const dedicated = (deployments.data?.items ?? []).filter((d) => d.kind === 'dedicated');
  const details = useQueries({ queries: dedicated.map((d) => deploymentQuery(d.id)) });
  const detailsError = details.find((q) => q.error)?.error;
  const settled = deployments.data !== undefined && details.every((q) => q.data !== undefined);
  const eligible = details.flatMap((q) => (q.data?.visibility.logs ? [q.data] : []));

  const header = <PageHeader title="Logs" subtitle="Your deployments only · last 30 days" />;
  if (deployments.error || detailsError)
    return (
      <>
        {header}
        <QueryState error={deployments.error ?? detailsError} />
      </>
    );
  if (!settled)
    return (
      <>
        {header}
        <QueryState error={undefined} />
      </>
    );
  if (eligible.length === 0)
    return (
      <>
        {header}
        <NotShared
          title="None of your deployments share logs"
          body="Rethink turns logs on per dedicated deployment. Deployments on a shared Rethink-hosted environment never offer logs. Health, uptime and incidents stay visible either way."
        />
      </>
    );
  return (
    <LogSearch
      header={header}
      deployments={eligible.map((d) => ({
        id: d.id,
        name: d.name,
        services: (d.services ?? []).map((s) => s.name),
      }))}
    />
  );
}

function LogSearch({
  header,
  deployments,
}: {
  header: ReactNode;
  deployments: Array<{ id: string; name: string; services: string[] }>;
}) {
  const [draft, setDraft] = useState<Draft>({
    ...initialDraft,
    environmentId: deployments[0]!.id,
  });
  const [applied, setApplied] = useState<LogQueryParams>(() => toParams(draft));
  const [fieldErrors, setFieldErrors] = useState<ReturnType<typeof logFilterErrors>>({});
  const result = useQuery({
    queryKey: ['logs', applied],
    queryFn: () => api.logs.query(applied),
    retry: false,
  });
  // Service labels seen in results, per deployment: labels belong to one site's stack.
  const [seenServices, setSeenServices] = useState<Record<string, string[]>>({});
  useEffect(() => {
    const env = applied.environmentId;
    const s = result.data?.lines.flatMap((l) => (l.service ? [l.service] : [])) ?? [];
    if (s.length)
      setSeenServices((prev) => ({
        ...prev,
        [env]: [...new Set([...(prev[env] ?? []), ...s])].sort(),
      }));
  }, [result.data, applied.environmentId]);

  if (result.error instanceof ApiError && result.error.code === 'logs_not_shared')
    return (
      <>
        {header}
        <NotShared
          title="Logs aren’t shared for your organization"
          body="Your organization hasn’t turned log access on for your role. Health, uptime and incidents stay visible either way. Message Rethink to change who can read logs."
        />
      </>
    );

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs = logFilterErrors(draft);
    setFieldErrors(errs);
    if (errs.service || errs.contains) return;
    setApplied(toParams(draft));
  };
  const current = deployments.find((d) => d.id === draft.environmentId);
  const services = [
    ...new Set([...(current?.services ?? []), ...(seenServices[draft.environmentId] ?? [])]),
  ];
  const lines = result.data?.lines ?? [];
  const appliedName = deployments.find((d) => d.id === applied.environmentId)?.name ?? '';

  return (
    <>
      <PageHeader
        title="Logs"
        subtitle="Your deployments only · last 30 days"
        actions={
          <Button
            variant="default"
            disabled={lines.length === 0}
            onClick={() =>
              downloadText(
                `logs-${appliedName.replace(/[^A-Za-z0-9_-]+/g, '-')}-${applied.end?.slice(0, 10)}.txt`,
                logLinesText(lines),
              )
            }
          >
            Download results
          </Button>
        }
      />
      {result.data && (
        <Alert color="blue" variant="light">
          Log access is on for your organization. Rethink sees the same lines when you send a
          report.
        </Alert>
      )}
      <form onSubmit={submit} noValidate aria-label="Search logs">
        <Card>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 12,
              alignItems: 'end',
            }}
          >
            <Select
              label="Deployment"
              data={deployments.map((d) => ({ value: d.id, label: d.name }))}
              value={draft.environmentId}
              onChange={(v) => v && set('environmentId', v)}
              allowDeselect={false}
            />
            <Autocomplete
              label="Service"
              placeholder="All services"
              data={services}
              value={draft.service}
              error={fieldErrors.service}
              onChange={(v) => set('service', v.trim())}
            />
            <Select
              label="Level"
              data={logLevelOptions}
              value={draft.level}
              onChange={(v) => set('level', (v ?? '') as Draft['level'])}
              allowDeselect={false}
            />
            <Select
              label="Time"
              data={logRangeOptions.map(({ value, label }) => ({ value, label }))}
              value={draft.range}
              onChange={(v) => v && set('range', v as LogRange)}
              allowDeselect={false}
            />
            <TextInput
              label="Contains"
              placeholder="Text to find"
              value={draft.contains}
              error={fieldErrors.contains}
              onChange={(e) => set('contains', e.currentTarget.value)}
            />
            <Button type="submit" loading={result.isFetching}>
              Search
            </Button>
          </div>
        </Card>
      </form>
      <Card padded={false}>
        {result.error ? (
          <div style={{ padding: 16 }}>
            <Alert color="red" title="Couldn’t search the logs">
              {errorMessage(result.error)}
            </Alert>
          </div>
        ) : !result.data ? (
          <div style={{ padding: 16 }}>
            <QueryState error={undefined} />
          </div>
        ) : lines.length === 0 ? (
          <p style={{ margin: 0, padding: '16px 20px', color: color.textMuted }}>
            No lines match in this time range.
          </p>
        ) : (
          <>
            <LogLines lines={lines} />
            {result.data.limited && (
              <p style={{ margin: 0, padding: '12px 20px', fontSize: 13, color: color.textMuted }}>
                Showing the newest {lines.length} lines. Narrow the search to see older ones.
              </p>
            )}
          </>
        )}
      </Card>
    </>
  );
}

function NotShared({ title, body }: { title: string; body: string }) {
  return (
    <Card title={title} style={{ maxWidth: 640 }}>
      <p style={{ margin: '0 0 16px', fontSize: 14, lineHeight: '21px', color: color.textBody }}>
        {body}
      </p>
      <Link
        to="/messages/new"
        search={{ kind: 'question', subject: 'Log access for our deployments' }}
        style={{ fontWeight: 600 }}
      >
        Message Rethink
      </Link>
    </Card>
  );
}
